import * as THREE from 'three';
import { WORLD, MODE, FROZEN_T } from './config.js';
import { World } from './collision.js';
import { buildPlan } from './city/layout.js';
import { buildCity, makeMaterials } from './city/build.js';
import { Player } from './player.js';
import { STATIONS, MOTION, stationById } from './cameras.js';
import { rngFor } from './rng.js';
import telem, { markReady, markBootError, frame, count, buildClock } from './telemetry.js';
import { makeRain, makeSteam, makeHalos, makeWetSmears, makeContactShadows, buildEnvironment } from './atmos.js';
import { Fauna, striderSpec, faunaRoutes, faunaMaterials } from './creatures.js';
import { Sentinel } from './robots.js';
import { makeCyborgs, Cyborg } from './cyborgs.js';
import { loadRobotBody } from './robotped.js';
import { Interactions } from './interact.js';
import { applyPose, poseReport, POSES, freeJoints } from './pose.js';
import { Engine } from './audio/engine.js';
import { setupPost } from './post.js';

const SYSTEM = 9; // Post-processing and polish

let renderer, scene, camera, player, world, plan;
let rain, steam, halos, smears, signMat, lightPool = [], lightSources = [];
let fauna = [];

// Solid creature collision (final brief): the machine dogs and the Sentinel are cylinders the
// player cannot pass through. ONE function serves the live loop and the gate probe, so the fence
// tests the exact code path the player runs -- not a copy of it.
const CREATURE_RADII = { fauna: 0.50, robot: 1.10 };
function pushPlayerOut(pos) {
  const solids = [];
  for (const f of fauna) solids.push([f.pos, CREATURE_RADII.fauna]);
  if (robot) solids.push([robot.pos, CREATURE_RADII.robot]);
  for (const [c, r] of solids) {
    const dx = pos.x - c.x, dz = pos.z - c.z;
    const d = Math.hypot(dx, dz);
    const min = r + 0.30;                       // + player radius
    if (d < min) {
      if (d > 1e-5) { pos.x = c.x + (dx / d) * min; pos.z = c.z + (dz / d) * min; }
      else pos.x = c.x + min;
    }
  }
}
let robot = null;
let inter = null;
let avatar = null;
let fill = null;
let cyborgs = [];
let glbBodyFactory = null;
const audio = new Engine();
let post = null;
let lastStepPhase = 0;
let clock, simT = 0, lastMs = performance.now();
let shotDone = false;

function makeSky() {
  // Night sky over a lit city: nearly black overhead, warm haze at the horizon from light pollution.
  // A flat background colour is one of the loudest CG tells in an outdoor night shot.
  const geo = new THREE.SphereGeometry(300, 24, 16);
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: {
      top: { value: new THREE.Color(0x05070d) },
      mid: { value: new THREE.Color(0x121722) },
      horizon: { value: new THREE.Color(0x2e2a30) },
    },
    vertexShader: `
      varying vec3 vPos;
      void main(){ vPos = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
    `,
    fragmentShader: `
      uniform vec3 top, mid, horizon;
      varying vec3 vPos;
      void main(){
        float h = normalize(vPos).y;
        vec3 c = mix(horizon, mid, smoothstep(-0.02, 0.30, h));
        c = mix(c, top, smoothstep(0.22, 0.85, h));
        gl_FragColor = vec4(c, 1.0);
      }
    `,
  });
  const m = new THREE.Mesh(geo, mat);
  m.name = 'sky';
  m.frustumCulled = false;
  return m;
}

/**
 * SYSTEM 3 — the lighting design. This replaces the System 1 scaffold wholesale, as the brief
 * requires: "emissive sign light as the primary source".
 *
 * Every light here is created FROM a source registered by the geometry that built its fitting
 * (`B.source(...)`), so a lamp and the light it casts cannot end up in different places. A critic
 * reported light pools "with no fixture anywhere in frame" three rounds running; that is what
 * authoring the two separately produces.
 *
 * LIGHT CULLING. The city registers ~55 sources. Forward rendering evaluates every light for every
 * fragment, so 55 point lights would blow the frame-time gate on its own. Instead a fixed POOL of
 * lights is re-targeted each frame to the nearest sources, which keeps shader cost constant while
 * the street still has a light at every fitting you can walk up to.
 */
const POOL_SIZE = 15;

function addLights(scene, sources) {
  // Sky bounce only. A night street is lit by its own signage, not by ambient -- the System 1
  // scaffold ran this at 0.62 to make untextured geometry legible and that is no longer the job.
    // Round 5: 0.16 was a global fill lifting every surface equally, which is what compressed the
  // whole frame into one midtone band. Unlit shadow has to be allowed to fall near black.
  // Final brief: ambient +0.15 so dark-metal recesses and panel seams stop collapsing to black.
  scene.add(new THREE.HemisphereLight(0x1d2740, 0x0a0a0c, 0.235));

  // A very dim cool key. Physically the overcast sky, not a moon. Its only job is to keep silhouettes
  // from going fully black and to give props a contact shadow.
  const key = new THREE.DirectionalLight(0x93aecc, 0.22);
  key.position.set(-24, 60, 18);
  key.target.position.set(0, 0, -4);
  key.castShadow = true;
  key.shadow.mapSize.set(MODE.mobile ? 1024 : 2048, MODE.mobile ? 1024 : 2048);
  const S = 46;
  key.shadow.camera.left = -S; key.shadow.camera.right = S;
  key.shadow.camera.top = S; key.shadow.camera.bottom = -S;
  key.shadow.camera.near = 1; key.shadow.camera.far = 180;
  key.shadow.bias = -0.0009;
  key.shadow.normalBias = 0.035;
  scene.add(key, key.target);

  // Per-kind photometry. Distance is generous so spill actually reaches the far kerb; decay 2 is
  // physical inverse-square.
  const KIND = {
    // A lit sign is a real source, and on a street like this the PRIMARY one.
    // Round 2: 88 cd across ~50 new sign sources washed the whole set pink -- the pool is only 15
    // lights, so on a signed street they crowd out every other fixture. A sign is a broad, dim
    // emitter, not a spotlight.
    sign:      { cd: 30, dist: 10 },
    shopfront: { cd: 62, dist: 17 },
    facade:    { cd: 26, dist: 13 },
    streetlamp:{ cd: 165, dist: 26 },
    walklamp:  { cd: 46, dist: 13 },
    bulkhead:  { cd: 34, dist: 11 },
    arcade:    { cd: 40, dist: 12 },
  };

  for (let i = 0; i < POOL_SIZE; i++) {
    const l = new THREE.PointLight(0xffffff, 0, 10, 2);
    l.visible = false;
    // The two NEAREST pool lights cast real shadows. A critic named the absence as the single
    // largest tell: "nothing in any frame casts a shadow... a phone at night produces violent
    // falloff and hard, dirty shadow shapes; this set produces flat uniform luminance, the most
    // reliable signature of a real-time render with no shadow-casting lights."
    //
    // Two, not fifteen: a point light costs SIX shadow renders (one per cube face), so the whole
    // pool casting would be 90 extra passes and an instant frame-time gate failure. The nearest two
    // are the ones whose shadows a viewer can actually resolve.
    if (i < 2) {
      // A POINT light shadow is six renders of the scene -- one per cube face. Two of them is
      // twelve extra passes, which is the difference between playable and not on a handset. The
      // key light keeps its shadow, so the scene still has grounded contact shadows.
      l.castShadow = !MODE.mobile;
      l.shadow.mapSize.set(512, 512);
      l.shadow.camera.near = 0.4;
      l.shadow.camera.far = 22;
      l.shadow.bias = -0.004;
      l.shadow.normalBias = 0.05;
    }
    scene.add(l);
    lightPool.push(l);
  }
  lightSources = sources.map((s) => ({ ...s, ph: KIND[s.kind] || KIND.facade }));
  return { key };
}

/** Re-target the pool at the nearest sources. Called every frame; cheap, O(n) over ~55. */
function retargetLights(camPos) {
  const scored = lightSources.map((s) => {
    const dx = s.pos[0] - camPos.x, dy = s.pos[1] - camPos.y, dz = s.pos[2] - camPos.z;
    return { s, d2: dx * dx + dy * dy + dz * dz };
  });
  scored.sort((a, b) => a.d2 - b.d2);
  for (let i = 0; i < lightPool.length; i++) {
    const l = lightPool[i];
    const hit = scored[i];
    if (!hit) { l.visible = false; continue; }
    const { s } = hit;
    l.visible = true;
    l.position.set(s.pos[0], s.pos[1], s.pos[2]);
    l.color.set(s.color);
    l.intensity = s.ph.cd * s.gain;
    l.distance = s.ph.dist;
  }
}

function boot() {
  const canvas = document.getElementById('view');

  renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance',
    preserveDrawingBuffer: true, // the harness reads pixels back for the non-blank check
  });
  // PINNED render scale. The overhaul brief requires this: adaptive or device-dependent scaling
  // self-stabilises frame time and would hide exactly the regressions the budget gates exist to
  // catch. 1.0 for every measurement, on every machine.
  // A phone's devicePixelRatio is 3 on the panel this ships to. Rendering a 1.76 M triangle
  // street at 3x is ~9x the fragment work of 1x for detail nobody can resolve at arm's length;
  // capping at 1.25 is the single biggest thing that makes this playable on a handset. Desktop
  // and shot mode are untouched, so every existing measurement stays comparable.
  const PIXEL_RATIO = MODE.shot ? 1.0
    : MODE.mobile ? Math.min(devicePixelRatio, 1.25)
      : Math.min(devicePixelRatio, 2);
  renderer.setPixelRatio(PIXEL_RATIO);
  telem.pixelRatio = PIXEL_RATIO;
  renderer.setSize(innerWidth, innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  // renderer.info auto-resets on EVERY render() call, so with an EffectComposer the counters
  // reflect only the LAST pass -- the fullscreen grade quad: 1 draw, 1 triangle. That silently
  // broke the draw-call and triangle telemetry the moment post-processing landed in System 9, and
  // the overhaul brief makes "draw calls must not increase" its primary weld-law fence. A fence
  // pinned at 1 can never fire. Manual reset once per frame accumulates every pass instead.
  renderer.info.autoReset = false;
  renderer.shadowMap.enabled = true;
  // PCFSoft samples the map many times per fragment. On mobile the soft variant alone can cost
  // more than the entire lighting pass, and at 1.25x scale the softness is not visible anyway.
  renderer.shadowMap.type = MODE.mobile ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;

  scene = new THREE.Scene();
  // Aerial perspective. A critic found it INVERTED: distant geometry was darker than foreground,
  // which flattens all depth. Real wet-city haze is lit by the city under it and lifts distant
  // blacks toward a dull grey-orange, so the fog colour must be BRIGHTER and warmer than the scene
  // it sits in front of -- not a darker blue, which is what was here.
  scene.fog = new THREE.FogExp2(0x241f1b, 0.0180);
  scene.add(makeSky());

  camera = new THREE.PerspectiveCamera(WORLD.FOV, innerWidth / innerHeight, WORLD.NEAR, WORLD.FAR);
  // HOR+ FRAMING. WORLD.FOV is a VERTICAL angle tuned for a 16:9 desktop. Hold it fixed on a
  // portrait phone (aspect ~0.54) and the horizontal view collapses to a slot -- the street
  // disappears and the player's own body fills the frame. Widen the vertical angle so the
  // HORIZONTAL angle is preserved, clamped so it never tips into fisheye.
  const applyFov = () => {
    const a = innerWidth / innerHeight;
    if (a >= 1.35) { camera.fov = WORLD.FOV; }
    else {
      const hRef = 2 * Math.atan(Math.tan((WORLD.FOV * Math.PI / 180) / 2) * 1.78);
      const v = 2 * Math.atan(Math.tan(hRef / 2) / a);
      camera.fov = Math.min(WORLD.FOV * 1.45, v * 180 / Math.PI);
    }
    camera.aspect = a;
    camera.updateProjectionMatrix();
  };
  applyFov();

  const buildT0 = performance.now();
  world = new World();
  plan = buildPlan();
  const mats = makeMaterials();

  // MODE.blank is the gate's negative control, not a feature. 1 = nothing at all, 2 = sky only.
  let built = { pieces: 0, buckets: 0 };
  if (MODE.blank === 1) {
    scene.clear();
    scene.background = new THREE.Color(0x11141c);
    scene.fog = null;
  } else if (MODE.blank === 2) {
    // sky sphere already added; deliberately no city, no lights
  } else if (MODE.debugPose || MODE.debugGait) {
    // WHITEBOX ROOM. The city is not built at all: no props, no rain, no steam, no wet smears, no
    // halos, no fog, no environment cube. Every one of those exists to sell the street, and every
    // one of them is something between the reviewer and the joint they are trying to look at.
    scene.clear();
    scene.background = new THREE.Color(0x2a2f38);
    scene.fog = null;
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(24, 24, 24, 24),
      new THREE.MeshStandardMaterial({ color: 0x9aa2ad, roughness: 0.92, metalness: 0.0 }));
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);
    // Tile lines, so foot placement and facing can be read against a known grid rather than
    // guessed against a flat colour. 1 m squares aligned to world X/Z.
    const grid = new THREE.GridHelper(24, 24, 0x5a636f, 0x6a7381);
    scene.add(grid);
    // A back wall gives the silhouette something to read against in the side view.
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(24, 10),
      new THREE.MeshStandardMaterial({ color: 0x7b838f, roughness: 0.95 }));
    wall.position.set(0, 5, -6);
    wall.receiveShadow = true;
    scene.add(wall);
    world.floor(-12, 12, -12, 12, 0, 'whitebox');
  } else {
    built = buildCity(scene, world, plan, mats);
    signMat = mats.sign;
    addLights(scene, built.sources);

    // Environment for wet reflections: a cube render of THIS city, so a puddle reflects the actual
    // shopfronts standing over it. An imported HDRI would break the zero-external-asset rule and a
    // flat colour would make every puddle identical.
    scene.environment = buildEnvironment(renderer, scene, new THREE.Vector3(0, 7.5, 2));
    scene.environmentIntensity = 0.42;   // the ground is now near-mirror in the pools; it needs
                                         // something real to return, and this cube map is the city
                                         // itself, so a puddle reflects the shopfronts above it.

    // Rain, steam and halos.
    rain = makeRain(14000);
    scene.add(rain.mesh);
    const vents = (built.vents || []).slice(0, 26);
    if (vents.length) { steam = makeSteam(vents); scene.add(steam.mesh); }
    const cs = makeContactShadows(built.grounded || []);
    if (cs) scene.add(cs);

    // --- System 5: robotic fauna ------------------------------------------
    // Built AFTER the world, because the gait samples world.groundAt for every footfall -- the
    // creature walks on the actual pavement, kerb and alley floor rather than on a flat plane.
    {
      const fm = faunaMaterials();
      faunaRoutes().forEach((route, i) => {
        fauna.push(new Fauna(scene, world, striderSpec(route, i), fm, 'strider' + i));
      });
      telem.fauna = fauna.length;
    }

    // --- System 6: the Sentinel -------------------------------------------
    robot = new Sentinel(scene, world, 'sentinel0');
    if (MODE.gait) robot.calm = true;   // gait review pass judges locomotion, not behaviour
    telem.robot = 1;

    // --- System 7: the population -----------------------------------------
    cyborgs = makeCyborgs(scene, world, glbBodyFactory);
    if (MODE.gait) for (const c of cyborgs) c.calm = true;
    telem.cyborgs = cyborgs.length;

    if (MODE.gait) {
      // Flat neutral review lighting. Deliberately ugly: this pass exists to make the MOTION
      // judgeable, not to look like the game.
      rain.mesh.visible = false;
      if (steam) steam.mesh.visible = false;
      scene.fog = null;
      scene.environmentIntensity = 0.30;
      scene.add(new THREE.HemisphereLight(0xdfe6f2, 0x5a6070, 2.4));
      const gk = new THREE.DirectionalLight(0xffffff, 2.2);
      gk.position.set(6, 9, 7);
      gk.castShadow = true;
      gk.shadow.mapSize.set(1024, 1024);
      gk.shadow.camera.left = -6; gk.shadow.camera.right = 6;
      gk.shadow.camera.top = 6; gk.shadow.camera.bottom = -6;
      gk.shadow.camera.near = 0.5; gk.shadow.camera.far = 40;
      gk.shadow.bias = -0.001;
      scene.add(gk);
    }

    smears = makeWetSmears(built.sources);
    scene.add(smears.mesh);
    halos = makeHalos(built.sources.map((sc) => ({
      pos: sc.pos, color: sc.color,
      size: sc.kind === 'streetlamp' ? 1.5 : sc.kind === 'shopfront' ? 1.15 : 0.75,
    })));
    scene.add(halos.mesh);

    // Drive the ground ripple clock. Same value every material reads, so ripples cannot desync.
    for (const m of Object.values(mats)) {
      if (m && m.userData && m.userData.rainUniforms) built.rainMats = true;
    }
  }

  if (MODE.debugPose || MODE.debugGait) {
    // Clear three-point fill: a key with a shadow so contact with the floor is legible, plus a
    // wide hemisphere so nothing falls into black. Deliberately flat -- this pass exists to make
    // GEOMETRY judgeable, not to look like the game.
    scene.add(new THREE.HemisphereLight(0xf0f4ff, 0x6a7180, 2.2));
    const k1 = new THREE.DirectionalLight(0xffffff, 2.4);
    k1.position.set(4, 8, 7);
    k1.castShadow = true;
    k1.shadow.mapSize.set(1024, 1024);
    k1.shadow.camera.left = -4; k1.shadow.camera.right = 4;
    k1.shadow.camera.top = 4; k1.shadow.camera.bottom = -4;
    k1.shadow.camera.near = 0.5; k1.shadow.camera.far = 20;
    scene.add(k1, k1.target);
    const k2 = new THREE.DirectionalLight(0xcfe0ff, 0.9);
    k2.position.set(-6, 4, 3);
    scene.add(k2);
  }

  player = new Player(world, camera, canvas);

  // PLAYER FILL. Some alleys and stairwells are mathematically black -- correct for the lighting
  // model, unplayable for a person. This is a chest-lamp-scale bounce that travels with the body.
  //
  // UNITS: the brief asks for "intensity 0.3-0.5", but this scene runs in CANDELA (the dimmest
  // real source in the city is a 26 cd sign, a streetlamp is 165). 12 cd over an 11 m falloff is
  // the same INTENT expressed in the scene's own units: it lifts nearby geometry off pure black
  // and leaves the neon contrast alone. It casts no shadows -- a point light costs six shadow
  // renders per frame, which is a frame-gate failure on its own.
  fill = new THREE.PointLight(0xffd8b4, 12, 11, 2);
  fill.castShadow = false;
  scene.add(fill);

  const st = stationById(MODE.cam);
  if (st) player.parkAt(st);
  else if (MODE.cam === 5) player.pos.set(MOTION.from[0], WORLD.KERB_H, MOTION.from[2]);

  // THE PLAYER IS A CYBORG (unification brief). Same class, same procedural archetype, same
  // Leg/Gait/IK -- constructed with driven:true so a controller supplies pos/heading/speed where
  // an NPC would consult its route. Built in every mode so the interaction arm and the gait clock
  // behave identically whether or not anyone is looking at it; hidden when the camera is inside
  // its head.
  if (MODE.debugGait) {
    // Walking stress test in the whitebox. Fixed timestep, driven from the harness, so every
    // frame of the contact sheet is reproducible on any machine.
    avatar = new Cyborg(scene, world, 'player', {
      driven: true, post: [0, -7.0], facing: 0, exaggerated: MODE.debugGait === 'exaggerated' });
    avatar.calm = true;
    avatar.heading = 0;
    freeJoints(avatar);   // the walking hinge caps would clamp a goose-step into a normal walk
    // RIDE HEIGHT. The shipping stance is 99.8% of full leg extension (a deliberate near-straight
    // leg, from the master brief's "+5 deg knee"). That leaves nothing in hand for a long stride:
    // reaching a foot planted 0.27 m behind a hip 0.888 m up needs sqrt(0.888^2 + 0.27^2) = 0.928
    // from an 0.889 m leg, so the solver clamps and the "planted" foot creeps. Standing 10% lower
    // gives the stance knee somewhere to go. This is a property of stride vs leg length, not of
    // the exaggerated mode -- the shipping walk carries the same limit at a smaller magnitude
    // (0.073 m, inside its 0.08 fence).
    avatar.ride = (avatar.legs[0].spec.upper + avatar.legs[0].spec.lower) * 0.90;
    const SPEED = 0.8 * avatar.gait.stride;   // hold the 0.8 Hz law
    let t = 0;
    const place = (view) => {
      const p = avatar.pos, y = p.y + 0.25;
      if (view === 'front') { camera.position.set(p.x, y, p.z + 3.4); camera.lookAt(p.x, y - 0.15, p.z); }
      else { camera.position.set(p.x - 3.4, y, p.z); camera.lookAt(p.x, y - 0.15, p.z); }
    };
    place('side');
    window.__NP_GAIT = {
      /** Advance the walk by dt with a FIXED step; the harness owns the clock. */
      step: (dt) => {
        const h = 1 / 120;
        for (let a = 0; a < dt - 1e-9; a += h) {
          t += h;
          avatar.pos.z += SPEED * h;
          avatar.drive(h, avatar.pos, 0, SPEED);
        }
        return +t.toFixed(3);
      },
      cam: (v) => { place(v); return v; },
      cycle: () => 1 / (SPEED * avatar.gait.rate),   // seconds per full stride cycle
      /** Per-leg state for the stance/knee/axis fences. */
      sample: () => {
        avatar.root.updateMatrixWorld(true);
        const rep = poseReport(avatar);
        return {
          t: +t.toFixed(3), phase: +avatar.gait.t.toFixed(4),
          pos: [+avatar.pos.x.toFixed(4), +avatar.pos.y.toFixed(4), +avatar.pos.z.toFixed(4)],
          legs: avatar.legs.map((L, i) => ({
            side: L.spec.hip[0] < 0 ? 'right' : 'left',
            contact: L.contact,
            knee: +L.shin.rotation.x.toFixed(4),
            planted: [+L.planted.x.toFixed(4), +L.planted.y.toFixed(4), +L.planted.z.toFixed(4)],
            footWorld: [+L.foot.matrixWorld.elements[12].toFixed(4),
              +L.foot.matrixWorld.elements[13].toFixed(4), +L.foot.matrixWorld.elements[14].toFixed(4)],
            footFwd: rep.legs[i].footFwd, footUp: rep.legs[i].footUp,
            ikErr: rep.legs[i].reach,
          })),
          arms: avatar.arms.map((A, i) => ({
            side: A.position.x < 0 ? 'right' : 'left',
            pitch: +A.rotation.x.toFixed(4), roll: +A.rotation.z.toFixed(4), yaw: +A.rotation.y.toFixed(4),
            axis: rep.arms[i].armDown,
          })),
          hipRoll: +avatar.body.rotation.z.toFixed(4), hipYaw: +avatar.body.rotation.y.toFixed(4),
        };
      },
    };
  }
  if (MODE.debugPose) {
    // One subject, one clear spot, one frozen pose. It uses the SAME rig the player and the NPCs
    // use, so anything this pass proves is proven about the shipping character, not a stand-in.
    avatar = new Cyborg(scene, world, 'player', { driven: true, post: [0, 0], facing: 0 });
    avatar.calm = true;
    avatar.heading = 0;                       // face +Z, so world axes ARE the body's axes
    avatar.pos.set(0, world.groundAt(0, 0, 0) + avatar.ride, 0);
    freeJoints(avatar);   // inspect the joint, do not inspect the walking clamp
    const name = applyPose(avatar, MODE.pose);
    // Three orthogonal stations around the chest.
    const c = { x: avatar.pos.x, y: avatar.pos.y + 0.35, z: avatar.pos.z };
    const D = 3.1;
    if (MODE.poseCam === 'side') { camera.position.set(c.x - D, c.y, c.z); }
    else if (MODE.poseCam === 'top') { camera.position.set(c.x, c.y + D, c.z + 0.001); }
    else { camera.position.set(c.x, c.y, c.z + D); }
    camera.lookAt(c.x, c.y, c.z);
    window.__NP_POSE = {
      name, id: MODE.pose, cam: MODE.poseCam,
      report: () => poseReport(avatar),
      list: () => Object.entries(POSES).map(([k, v]) => ({ id: +k, name: v.name })),
      /** Re-apply on demand so a single page can sweep every pose without reloading. */
      set: (id) => { const n = applyPose(avatar, id); window.__NP_POSE.name = n; return n; },
    };
  }
  // debugGait/debugPose build their own subject above; without excluding them here a SECOND,
  // plain avatar was constructed and silently replaced it -- the goose-step test then measured
  // a normal walk (peak arm pitch -24 deg instead of -89) and blamed the rig.
  if (!MODE.gait && !MODE.track && !MODE.debugPose && !MODE.debugGait) {
    avatar = new Cyborg(scene, world, 'player', {
      driven: true, post: [player.pos.x, player.pos.z], facing: player.yaw });
    avatar.calm = true;                      // the avatar does not react to itself
    avatar.root.visible = MODE.view === 'tp';
    inter = new Interactions(scene, camera, player, fauna, cyborgs, count, avatar);
  }

  telem.build = { pieces: built.pieces, buckets: built.buckets, ...world.stats() };

  if (!MODE.shot) {
    // Audio starts on the first gesture -- creating an AudioContext earlier logs an autoplay
    // warning, and the console gate is zero-tolerance. Shot mode stays silent and deterministic.
    const startAudio = () => { audio.start(); };
    window.addEventListener('keydown', startAudio, { once: true });
    canvas.addEventListener('click', startAudio, { once: true });
  }

  addEventListener('resize', () => {
    applyFov();
    renderer.setSize(innerWidth, innerHeight, false);
    if (post) post.setSize(innerWidth, innerHeight);
  });

  buildClock(buildT0);

  post = setupPost(renderer, scene, camera, innerWidth, innerHeight);

  clock = new THREE.Clock();
  requestAnimationFrame(loop);
}

function loop() {
  requestAnimationFrame(loop);
  const now = performance.now();
  const dtMs = now - lastMs;
  lastMs = now;
  const dt = Math.min(0.05, clock.getDelta());

  if (MODE.shot) {
    if (!shotDone) {
      if (MODE.cam === 5) {
        // Walk the fixed path with a FIXED timestep so the mid-motion frame is identical every
        // round regardless of how fast the capture machine is.
        const step = 1 / 60;
        let t = 0;
        player.yaw = MOTION.yaw;
        while (t < MOTION.atT) { player.driveTo(step, MOTION.to[0], MOTION.to[2]); t += step; }
      }
      // Advance the fauna to the frozen time with a FIXED step. A variable step would give a
      // different pose on a faster machine, and the whole point of a capture station is that two
      // rounds are comparable.
      const step = 1 / 60;
      for (let t = 0; t < FROZEN_T; t += step) {
        for (const f of fauna) {
          const ev = f.update(step, player.pos);
          for (const k in ev) count(k, ev[k]);
        }
        if (robot) {
          const rev = robot.update(step, player.pos);
          for (const k in rev) count(k, rev[k]);
        }
        for (const c of cyborgs) {
          const cev = c.update(step, player.pos);
          for (const k in cev) count(k, cev[k]);
        }
      }
      // Station 6 tracks the fauna for the contact sheet.
      //
      // The offset MUST be toward the street centreline. The first version used the raw perpendicular
      // (-cos h, +sin h), which on the west-side route put the camera at x = -8.95 -- 0.95 m inside
      // the building -- and the critic correctly refused to score six frames of wall.
      if (MODE.cam === 6) {
        // Contact-sheet camera. Placement is per-track, because each subject lives in a different
        // clear corridor: fauna on the sidewalk band [5.35, 7.6] (two failed attempts put the
        // camera inside a wall, then on a car roof); the robot patrols the roadway at |x| = 2.2,
        // so its camera sits on the OPPOSITE roadway lane -- nothing tall lives on the centreline.
        if (MODE.track === 'cyborg' && cyborgs.length) {
          const c0 = cyborgs[0];
          const side = c0.pos.x < 0 ? -1 : 1;
          const clamped = Math.min(7.6, Math.max(5.35, Math.abs(c0.pos.x) - 1.15));
          camera.position.set(side * clamped, c0.pos.y + 0.55, c0.pos.z + 1.35);
          camera.lookAt(c0.pos.x, c0.pos.y + 0.2, c0.pos.z);
        } else if (MODE.track === 'robot' && robot) {
          const side = robot.pos.x < 0 ? 1 : -1;
          camera.position.set(robot.pos.x + side * 4.4, robot.pos.y + 0.4, robot.pos.z + 1.7);
          camera.lookAt(robot.pos.x, robot.pos.y + 0.1, robot.pos.z);
        } else if (fauna.length) {
          const f = fauna[0];
          const side = f.pos.x < 0 ? -1 : 1;
          const clamped = Math.min(7.6, Math.max(5.35, Math.abs(f.pos.x) - 1.25));
          camera.position.set(side * clamped, f.pos.y + 0.62, f.pos.z + 1.55);
          camera.lookAt(f.pos.x, f.pos.y + 0.26, f.pos.z);
        }
      }
      simT = FROZEN_T;
      if (lightSources.length) retargetLights(camera.position);
      if (rain) rain.update(simT, camera.position);
      if (steam) steam.update(simT);
      if (smears) smears.update(simT, camera.position);
  if (signMat && signMat.userData.shader) signMat.userData.shader.uniforms.uSignTime.value = simT;
      shotDone = true;
    }
  } else {
    simT += dt;
    player.update(dt);
    for (const f of fauna) {
      const ev = f.update(dt, player.pos);
      for (const k in ev) count(k, ev[k]);
    }
    for (const c of cyborgs) {
      const cev = c.update(dt, player.pos);
      for (const k in cev) count(k, cev[k]);
    }
    if (avatar && MODE.debugGait) {
      // clock is owned by __NP_GAIT.step()
    } else if (avatar && MODE.debugPose) {
      // Re-assert the pose every frame: nothing else may touch this rig.
      applyPose(avatar, MODE.pose);
    } else if (avatar) {
      avatar.drive(dt, player.pos, player.yaw + Math.PI, player.moveSpeed || 0);
      // First person rides the same body: when the avatar bends to reach something low, the lens
      // goes down with it, so the FP arms are never asked to touch what the body cannot.
      player.reachDip = avatar.reachCrouch + Math.sin(avatar.reachLean) * 0.34;
    }
    if (inter && player.touch) {
      // The ACT button mirrors the [E] cue exactly: present only when something is in range.
      const cue = document.getElementById('npcue');
      const showing = cue && cue.style.opacity === '1';
      player.touch.setAction(showing ? (inter.cand && inter.cand.kind === 'dog'
        ? (inter.nextDog === 'pet' ? 'PET' : 'SHAKE') : 'SHAKE') : null);
    }
    if (fill) {
      // Chest height, a little ahead of the body, so it grazes what you are walking toward.
      fill.position.set(
        player.pos.x - Math.sin(player.yaw) * 0.30,
        player.pos.y + 1.25,
        player.pos.z - Math.cos(player.yaw) * 0.30);
    }
    if (inter) inter.update(dt);
    if (robot) {
      const rev = robot.update(dt, player.pos);
      for (const k in rev) count(k, rev[k]);
      // No consequence handler on purpose (user decision, third brief on this): the
      // fade-to-white + respawn that lived here is DELETED. Being cornered by the Sentinel's
      // confront IS the outcome -- red lamp, blocked path, zero reset.
    }
    pushPlayerOut(player.pos);
    audio.update(player.pos.x, player.pos.z, player.yaw, robot);
    {
      // A step lands each half bob cycle (bobT advances 4.6/s at walk speed). Surface comes from
      // where the player actually stands, so the stair grate rings and the arcade tile clicks --
      // the brief's "footsteps that change with surface".
      const phase = Math.floor(player.bobT / Math.PI);
      if (phase !== lastStepPhase && player.bobAmt > 0.4) {
        lastStepPhase = phase;
        const px = player.pos.x, pz = player.pos.z, py = player.pos.y;
        let surf = 'pavement';
        if (py > 1.0) surf = 'grate';                                    // stair or walkway deck
        else if (px > 8 && pz > -25.5 && pz < -15.2) surf = 'tile';      // arcade concourse
        else if (Math.abs(px) <= 5) surf = 'asphalt';
        audio.step(surf);
      }
    }
  }

  if (rain) rain.update(simT, camera.position);
  if (steam) steam.update(simT);
  if (smears) smears.update(simT, camera.position);
  if (signMat && signMat.userData.shader) signMat.userData.shader.uniforms.uSignTime.value = simT;
  if (lightSources.length) retargetLights(camera.position);
  if (window.__NP_RAIN) window.__NP_RAIN.value = simT;

  // Time the render itself. rAF deltas are pinned at 16.6 ms by vsync, so gating on them measures
  // the display refresh rate and not the work -- an instrument that cannot fail until it fails all
  // at once. This is the number the frame-time gate actually reads.
  const r0 = performance.now();
  renderer.info.reset();
  // The negative controls render WITHOUT post: grain legitimately puts edge energy on a blank
  // frame, which would blind the blank-fence's calibration. The control exists to prove the SCENE
  // detector fires on an empty scene -- film stock is not scene content.
  if (post && !MODE.blank) {
    // Motion vector: station 5 walks north at 1.55 m/s (three critics named the missing blur in
    // "the frame named motion"); free play uses the player's own speed. Grain time is simT, which
    // shot mode freezes -- deterministic per station.
    let mx = 0, my = 0;
    if (MODE.shot && MODE.cam === 5) { mx = 0.0135; my = 0.004; }
    else if (!MODE.shot && player.bobAmt > 0.3) {
      // Walking blur cut ~65% (0.006 -> 0.0021) and hard-capped. The blur is a speed HINT, not a
      // readability tax: at a walking pace the street has to stay legible. The station-5 motion
      // frame keeps its original 0.0135 so that evidence stays comparable to its baseline.
      my = Math.min(0.0021, 0.0021 * player.bobAmt);
    }
    post.frame(simT, mx, my);
    post.composer.render();
  } else {
    renderer.render(scene, camera);
  }
  frame(dtMs, renderer, performance.now() - r0);

  if (!telem.ready && telem.frames >= 3) markReady(SYSTEM);
  if (!window.__NP_DBG && !MODE.shot) {
    // Evidence hook (free play only): park the player somewhere and read creature state, so a
    // capture script can wait for a behaviour to actually happen before the shutter.
    window.__NP_DBG = {
      tp: (x, z, yaw, pitch) => { player.pos.x = x; player.pos.z = z; if (yaw != null) player.yaw = yaw; if (pitch != null) player.pitch = pitch; },
      /** Camera yaw, for the touch-look fence. */
      yaw: () => +player.yaw.toFixed(4),
      /** How many lights actually cast shadows, by type -- the mobile tier's main claim. */
      shadowCasters: () => {
        let point = 0, dir = 0;
        scene.traverse((o) => {
          if (!o.isLight || !o.castShadow) return;
          if (o.isPointLight) point++; else dir++;
        });
        return { point, dir };
      },
      /** Toggle the player fill light, so its effect can be MEASURED rather than asserted. */
      fill: (on) => { if (fill) fill.visible = !!on; return fill ? fill.visible : null; },
      fauna0: () => ({ x: +fauna[0].pos.x.toFixed(2), z: +fauna[0].pos.z.toFixed(2),
        heading: +fauna[0].heading.toFixed(3), state: fauna[0].state, speed: +fauna[0].speed.toFixed(3) }),
      /** Pedestrian evidence: is each brain wearing the GLB body, and is its Walk clip alive? */
      peds: () => cyborgs.map((c) => ({
        key: c.key, mode: c.mode, x: +c.pos.x.toFixed(2), z: +c.pos.z.toFixed(2),
        glb: !!c.glb,
        walkW: c.glb ? +c.glb.walkWeight.toFixed(3) : null,
        clipT: c.glb ? +c.glb.walk.time.toFixed(3) : null,
        handY: c.glb ? +c.glb.handTip.getWorldPosition(new THREE.Vector3()).y.toFixed(3) : null,
      })),
    };
  }
  if (!window.__NP_COLLIDE && fauna.length) {
    window.__NP_COLLIDE = {
      /** Walk a test point straight at the creature centre through the SAME pushback the player
       *  runs; report the closest approach with and without the pushback. The naive distance
       *  proves the fence can fail; the guarded distance proves the wall is solid. */
      probe: (kind) => {
        const target = kind === 'robot' ? robot : fauna[0];
        const r = CREATURE_RADII[kind === 'robot' ? 'robot' : 'fauna'];
        const p = { x: target.pos.x + r + 2.0, y: 0, z: target.pos.z };
        let naive = Infinity, guarded = Infinity;
        const np = { x: p.x, z: p.z };
        for (let i = 0; i < 200; i++) {
          for (const q of [p, np]) {
            const dx = target.pos.x - q.x, dz = target.pos.z - q.z;
            const d = Math.hypot(dx, dz);
            if (d > 1e-4) { q.x += (dx / d) * 0.03; q.z += (dz / d) * 0.03; }
          }
          pushPlayerOut(p);
          guarded = Math.min(guarded, Math.hypot(p.x - target.pos.x, p.z - target.pos.z));
          naive = Math.min(naive, Math.hypot(np.x - target.pos.x, np.z - target.pos.z));
        }
        return { guarded: +guarded.toFixed(3), naive: +naive.toFixed(3), min: +(r + 0.30).toFixed(3) };
      },
      /** Stand the player 1.2 m ahead of fauna[0] for 3 s; the friendly-halt fence requires a
       *  full smooth stop -- not a shove, not a clip, not a reset. */
      faunaHalt: () => {
        const f = fauna[0];
        const step = 1 / 60;
        f.state = 'errand'; f.noticed = false;
        for (let t = 0; t < 3.0; t += step) {
          const p = { x: f.pos.x + Math.sin(f.heading) * 1.2, y: 0.15, z: f.pos.z + Math.cos(f.heading) * 1.2 };
          const ev = f.update(step, p);
          for (const k in ev) count(k, ev[k]);
        }
        return { state: f.state, speed: +f.speed.toFixed(3) };
      },
      /** Drive fauna[0] along its real route for 900 steps (player far away) and report the WORST
       *  penetration of its capsule into any world solid -- bins, bollards, parked cars. */
      faunaWorld: () => {
        const f = fauna[0];
        const step = 1 / 60;
        let worst = 0;
        for (let i = 0; i < 900; i++) {
          const ev = f.update(step, { x: 500, y: 0, z: 500 });
          void ev;
          worst = Math.max(worst, world.penetration(f.pos.x, f.pos.z, 0.40, f.pos.y + 0.1, f.pos.y + 0.8));
        }
        return { worstPen: +worst.toFixed(3) };
      },
    };
  }
  if (!window.__NP_INTERACT && inter) {
    window.__NP_INTERACT = {
      run: (kind, opts) => inter.debugRun(kind, opts),
      view: () => MODE.view,
      /** Screen pixels of the two clasping hands + both bodies. Settles "are they touching" and
       *  "who is who in this frame" without anyone squinting at a dark screenshot. */
      clasp: () => {
        const c = cyborgs.find((x) => x.interact) || null;
        const px = (o) => {
          const v = o.getWorldPosition(new THREE.Vector3()).project(camera);
          return [Math.round((v.x * 0.5 + 0.5) * innerWidth), Math.round((-v.y * 0.5 + 0.5) * innerHeight),
            +v.z.toFixed(3)];
        };
        return {
          npcFound: !!c,
          npcBody: c ? px(c.root) : null,
          npcHand: c && c.handTip ? px(c.handTip) : null,
          avatarBody: avatar ? px(avatar.root) : null,
          avatarHand: avatar && avatar.handTip ? px(avatar.handTip) : null,
          npcLean: c ? [+c.reachLean.toFixed(3), +c.reachCrouch.toFixed(3)] : null,
          avatarLean: avatar ? [+avatar.reachLean.toFixed(3), +avatar.reachCrouch.toFixed(3)] : null,
          handGapM: (c && c.handTip && avatar && avatar.handTip)
            ? +c.handTip.getWorldPosition(new THREE.Vector3())
              .distanceTo(avatar.handTip.getWorldPosition(new THREE.Vector3())).toFixed(3) : null,
        };
      },
      avatar: () => (avatar ? {
        pos: [+avatar.pos.x.toFixed(2), +avatar.pos.y.toFixed(2), +avatar.pos.z.toFixed(2)],
        heading: +avatar.heading.toFixed(3), mode: avatar.mode, visible: avatar.root.visible,
        rightArm: avatar.rightArm, shoulderX: +avatar.arms[avatar.rightArm].position.x.toFixed(3),
        camDist: +camera.position.distanceTo(avatar.pos).toFixed(2),
      } : null),
      /** Screen-pixel positions of the arms in play. "Which side is that arm on" is a question
       *  about PIXELS, and reading it off a dark screenshot is how two people end up disagreeing. */
      screen: () => {
        const c = cyborgs.find((x) => x.mode === 'idle') || cyborgs[0];
        const px = (o) => {
          const v = o.getWorldPosition(new THREE.Vector3()).project(camera);
          return [Math.round((v.x * 0.5 + 0.5) * innerWidth), Math.round((-v.y * 0.5 + 0.5) * innerHeight)];
        };
        const out = { w: innerWidth, npcArms: [], fp: {} };
        c.arms.forEach((sh, i) => out.npcArms.push({
          i, localX: +sh.position.x.toFixed(3), side: sh.position.x < 0 ? 'RIGHT' : 'left',
          shoulderPx: px(sh), handPx: px(c.elbows[i]), pitch: +sh.rotation.x.toFixed(2),
        }));
        out.npcHandTip = c.handTip ? px(c.handTip) : null;
        if (inter.arms) for (const a of inter.arms) {
          out.fp[a.side === 1 ? 'right' : 'left'] = { rootPx: px(a.root), wristPx: px(a.hand) };
        }
        return out;
      },
    };
  }
  if (!window.__NP_ROBOT && robot) {
    window.__NP_ROBOT = {
      state: () => robot.state,
      /**
       * Drive the robot with a synthetic player and report which events ACTUALLY fired. Each mode
       * starts from reset() so the result depends only on the placement under test, not on
       * whatever the 12 s shot warm-up left behind.
       */
      probe: (mode) => {
        const step = 1 / 60;
        const before = { ...telem.events };
        robot.reset();
        const sim = (sec, posFn) => {
          for (let t = 0; t < sec; t += step) {
            const ev = robot.update(step, posFn());
            for (const k in ev) count(k, ev[k]);
          }
        };
        const ahead = (d) => () => ({
          x: robot.pos.x + Math.sin(robot.heading) * d, y: 0.15,
          z: robot.pos.z + Math.cos(robot.heading) * d,
        });
        if (mode === 'far') sim(3.0, ahead(40));
        else if (mode === 'near') sim(6.0, ahead(7));
        else if (mode === 'confront') { sim(4.0, ahead(7)); sim(3.0, ahead(0.9)); }
        else if (mode === 'escape') { sim(6.0, ahead(7)); sim(9.0, () => ({ x: robot.pos.x + 42, y: 0.15, z: robot.pos.z + 30 })); }
        const delta = {};
        for (const k in telem.events) {
          const d = telem.events[k] - (before[k] || 0);
          if (d > 0) delta[k] = d;
        }
        return { delta, state: robot.state };
      },
    };
    window.__NP_TRACK = {
      /** Max |achieved foot - target| across the tracked creature's legs, in metres. Independent
       *  measurement: world position of the solved chain end, not the number handed to the solver. */
      ikError: () => {
        const t = MODE.track === 'robot' && robot ? robot : MODE.track === 'cyborg' && cyborgs.length ? cyborgs[0] : fauna[0];
        t.root.updateMatrixWorld(true);
        const v = new THREE.Vector3();
        let m = 0;
        for (const L of t.legs) {
          L.foot.getWorldPosition(v);
          m = Math.max(m, v.distanceTo(L.target));
        }
        return +m.toFixed(4);
      },
      /** Diagnostic: per-leg achieved/target/hip numbers, for hunting fence failures. */
      __detail: () => {
        const t = MODE.track === 'robot' && robot ? robot : MODE.track === 'cyborg' && cyborgs.length ? cyborgs[0] : fauna[0];
        t.root.updateMatrixWorld(true);
        const v = new THREE.Vector3(), h = new THREE.Vector3();
        return t.legs.map((L) => {
          L.foot.getWorldPosition(v);
          L.hip.getWorldPosition(h);
          return {
            knee: L.spec.knee, contact: L.contact,
            err: +v.distanceTo(L.target).toFixed(3),
            target: [+L.target.x.toFixed(2), +L.target.y.toFixed(2), +L.target.z.toFixed(2)],
            got: [+v.x.toFixed(2), +v.y.toFixed(2), +v.z.toFixed(2)],
            hipToTarget: +h.distanceTo(L.target).toFixed(3),
            maxReach: +(L.spec.upper + L.spec.lower).toFixed(3),
          };
        });
      },
      contacts: () => [(MODE.track === 'robot' && robot ? robot : MODE.track === 'cyborg' && cyborgs.length ? cyborgs[0] : fauna[0]).contactCount()],
      poses: () => [(MODE.track === 'robot' && robot ? robot : MODE.track === 'cyborg' && cyborgs.length ? cyborgs[0] : fauna[0]).legs.map((L) => [
        +L.target.x.toFixed(4), +L.target.y.toFixed(4), +L.target.z.toFixed(4)])],
    };
  }
  if (!window.__NP_CYBORG && cyborgs.length) {
    window.__NP_CYBORG = {
      /** Walker 0, driven with a synthetic player. near = planted in its path; far = 40 m off. */
      probe: (mode) => {
        const c = cyborgs[0];
        const step = 1 / 60;
        const before = { ...telem.events };
        c.noticed = false; c.sideTarget = 0; c.sideStep = 0;
        const posFn = mode === 'near'
          ? () => ({ x: c.pos.x + Math.sin(c.heading) * 1.2, y: 0.15, z: c.pos.z + Math.cos(c.heading) * 1.2 })
          : () => ({ x: c.pos.x + 40, y: 0.15, z: c.pos.z });
        let maxSide = 0;
        for (let t = 0; t < 3.0; t += step) {
          const ev = c.update(step, posFn());
          for (const k in ev) count(k, ev[k]);
          maxSide = Math.max(maxSide, Math.abs(c.sideStep));
        }
        const delta = {};
        for (const k in telem.events) {
          const d = telem.events[k] - (before[k] || 0);
          if (d > 0) delta[k] = d;
        }
        return { delta, maxSide: +maxSide.toFixed(3) };
      },
      /** Idler (index 5), same near/far contract for the glance. */
      probeIdle: (mode) => {
        const c = cyborgs[5];
        const step = 1 / 60;
        const before = { ...telem.events };
        c.noticed = false;
        const posFn = mode === 'near'
          ? () => ({ x: c.pos.x + 1.4, y: 0.15, z: c.pos.z + 0.8 })
          : () => ({ x: c.pos.x + 40, y: 0.15, z: c.pos.z });
        for (let t = 0; t < 2.0; t += step) {
          const ev = c.update(step, posFn());
          for (const k in ev) count(k, ev[k]);
        }
        const delta = {};
        for (const k in telem.events) {
          const d = telem.events[k] - (before[k] || 0);
          if (d > 0) delta[k] = d;
        }
        return { delta, headYaw: +c.headYaw.toFixed(3) };
      },
    };
  }
  if (!window.__NP_FAUNA) {
    // The interaction gate needs to drive a creature and observe the result. Exposing the objects
    // is the only way the gate can test the BEHAVIOUR rather than test that a counter increments.
    window.__NP_FAUNA = {
      count: () => fauna.length,
      contacts: () => fauna.map((f) => f.contactCount()),
      states: () => fauna.map((f) => f.state),
      poses: () => fauna.map((f) => f.legs.map((L) => [+L.target.x.toFixed(4), +L.target.y.toFixed(4), +L.target.z.toFixed(4)])),
      /** Walk the player to a creature and step the sim; returns what actually fired. */
      probe: (seconds, standOff) => {
        const f = fauna[0];
        if (!f) return null;
        const before = { ...telem.events };
        const step = 1 / 60;
        for (let t = 0; t < seconds; t += step) {
          const p = { x: f.pos.x + (standOff || 0), y: f.pos.y + 1.2, z: f.pos.z + 0.6 };
          const ev = f.update(step, p);
          for (const k in ev) count(k, ev[k]);
        }
        const after = { ...telem.events };
        const delta = {};
        for (const k in after) delta[k] = after[k] - (before[k] || 0);
        return { delta, state: f.state, headYaw: +f.headYaw.toFixed(4) };
      },
    };
  }
}

try {
  // The pedestrian body (the owner's rigged robot from mesh-forge-ai) loads BEFORE boot so the
  // population can wear it from frame one. If the asset is missing or fails to parse, the city
  // falls back to the procedural bodies rather than failing to boot - the robot is a costume,
  // not a dependency.
  loadRobotBody('./assets/robot_ped.glb')
    .then((f) => { glbBodyFactory = f; })
    .catch((e) => { console.warn('robot body unavailable, procedural fallback:', e); })
    .finally(() => boot());
} catch (e) {
  markBootError(e);
  throw e;
}
